/**
 * VerifyEmailPage – Step 2 of the signup flow AND the OTP-based login page.
 *
 * Signup flow (signupStore.isActive() === true):
 *  - Calls POST /custom_account/verify-otp/ to verify the email
 *  - Does NOT log in – navigates to /personal-information as creator by default
 *
 * Login flow:
 *  - Current behaviour: looks for a token in the response and logs in
 *
 * Timer: 10-minute countdown (matching the mobile OtpBottomSheet _start = 10 * 60)
 *  - Shown as MM:SS while counting down
 *  - "Resend OTP" button only becomes active when timer reaches 0
 *  - Resend resets the timer
 */

import React, { useState, useRef, useEffect } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useAuth } from '../../hooks/useAuth';
import { useEmailLoginMutation, useSendOtpMutation } from '../../api/auth';
import { toast } from '../../utils/toast';
import { signupStore } from '../../utils/signupStore';
import { extractApiError, getRetryAfter, hasErrorCode } from '../../utils/apiError';
import { mapUserFromAuthResponse } from '../../contexts/AuthContext';
import { AuthHeader } from './AuthHeader';

// 10 minutes – matches mobile _start = 10 * 60
const TIMER_INITIAL = 10 * 60;

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: '/verify-email' });
  const { login } = useAuth();
  const emailLoginMutation = useEmailLoginMutation();
  const sendOtpMutation = useSendOtpMutation();

  const isSignupFlow = signupStore.isActive();
  const email = search.email || 'your email';

  // 5-digit numeric OTP (matching mobile Pinput length: 5)
  const [otp, setOtp] = useState(['', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Timer state – incrementing timerKey restarts the effect (used on resend)
  const [timeLeft, setTimeLeft] = useState(TIMER_INITIAL);
  const [timerKey, setTimerKey] = useState(0);
  // Cooldown (seconds) for the next timer run; the API can override it via `retry_after`
  const nextTimerSeconds = useRef(TIMER_INITIAL);

  // Countdown timer effect – mirrors mobile Timer.periodic(Duration(seconds: 1))
  useEffect(() => {
    setTimeLeft(nextTimerSeconds.current);
    nextTimerSeconds.current = TIMER_INITIAL;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerKey]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleVerify = async (fullOtp: string) => {
    if (fullOtp.length < 5) return;
    setIsLoading(true);
    try {
      const data = await emailLoginMutation.mutateAsync({ email, otp: fullOtp });

      if (isSignupFlow) {
        signupStore.set({ role: 'creator' });
        toast.success('Email verified successfully');
        // Email verified – default onboarding role to creator and continue profile setup
        navigate({ to: '/personal-information' });
        return;
      }

      // OTP login flow – handle nested { data, tokens } structure
      const authToken = data?.tokens?.access ?? data?.token ?? data?.access ?? data?.access_token;
      const refreshToken = data?.tokens?.refresh ?? data?.refresh ?? data?.refresh_token;

      if (authToken) {
        const userDataRaw = data?.data ?? data?.user ?? data;
        const mappedUser = mapUserFromAuthResponse(userDataRaw);
        login(authToken, mappedUser, refreshToken);
        toast.success('Login successful');
        if (mappedUser.profileIncomplete) {
          toast.info('Complete your profile details to unlock all features.');
        }

        navigate({ to: '/' });
      } else {
        toast.warn('Verification successful but no session token received.');
      }
    } catch (error: any) {
      console.error('OTP Verification Error:', error);
      // Clear OTP inputs on failure so user can re-enter
      setOtp(['', '', '', '', '']);
      inputRefs.current[0]?.focus();
      const msg = extractApiError(
        error,
        'Verification failed. Please check the code and try again.',
      );
      toast.error(msg);
      if (hasErrorCode(error, 'OTP_MAX_ATTEMPTS')) {
        // Code is no longer usable; force a resend (respecting any cooldown)
        nextTimerSeconds.current = getRetryAfter(error) ?? 0;
        setTimerKey((k) => k + 1);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (timeLeft > 0 || isLoading) return;
    try {
      await sendOtpMutation.mutateAsync({ email, type: 'registration' });
      setOtp(['', '', '', '', '']);
      setTimerKey((k) => k + 1); // restarts the timer effect
      // Success toast is handled by mutation's onSuccess
      inputRefs.current[0]?.focus();
    } catch (error) {
      // Toast is handled by mutation's onError; honour the server cooldown if provided
      const retryAfter = getRetryAfter(error);
      if (retryAfter !== undefined) {
        nextTimerSeconds.current = retryAfter;
        setTimerKey((k) => k + 1);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    if (!pasted) return;
    const newOtp = ['', '', '', '', ''];
    pasted.split('').forEach((c, i) => {
      if (i < 5) newOtp[i] = c;
    });
    setOtp(newOtp);
    const nextIdx = Math.min(pasted.length, 4);
    inputRefs.current[nextIdx]?.focus();
    if (pasted.length === 5) handleVerify(pasted);
  };

  const handleChange = (index: number, value: string) => {
    // Accept only digits, single character
    if (!/^\d?$/.test(value)) return;
    const digit = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    if (digit && index < 4) inputRefs.current[index + 1]?.focus();
    if (digit && index === 4 && newOtp.join('').length === 5) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-blue-500/30">
      {/* Background glow */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #2F80ED, #942FED)',
          opacity: 0.5,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      <AuthHeader />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-150 mx-auto px-6">
        <div className="w-full text-center flex flex-col items-center">
          {/* Back button */}
          <div className="w-full flex justify-start mb-6">
            <button
              onClick={() => window.history.back()}
              className="w-10 h-10 rounded-full bg-[#1a1a1a] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors border border-white/5 cursor-pointer shadow-lg"
              aria-label="Go back"
            >
              <FiArrowLeft className="text-zinc-300" size={18} />
            </button>
          </div>

          <h1
            className="text-white text-[32px] md:text-[40px] font-medium leading-[1.1] mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            Please check your email
          </h1>
          <p className="text-[#a1a1aa] text-[14px] md:text-[15px] mb-2 font-medium">
            We&apos;ve sent a code to <span className="text-white font-semibold">{email}</span>
          </p>
          <p className="text-zinc-500 text-[13px] mb-8">{email}</p>

          {/* 5-digit OTP inputs – matches mobile Pinput(length: 5) */}
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-6">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => {
                  inputRefs.current[index] = el;
                }}
                type="text"
                inputMode="numeric"
                disabled={isLoading}
                value={digit}
                onPaste={handlePaste}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                maxLength={1}
                className="w-12.5 h-12.5 md:w-14 md:h-14 rounded-xl bg-[#141414] text-white border border-white/5 focus:border-[#3B82F6] focus:outline-none text-center text-xl md:text-2xl shadow-lg transition-colors font-semibold disabled:opacity-50"
              />
            ))}
          </div>

          {/* Loading spinner */}
          {isLoading && (
            <div className="mb-4 flex justify-center">
              <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            </div>
          )}

          {/* Timer / Resend – matches mobile: show countdown while > 0, "Resend OTP" when = 0 */}
          <div className="flex items-center justify-center gap-2 text-sm mb-4">
            {timeLeft > 0 ? (
              <span className="text-[#3B82F6] font-medium">Resend in {formatTime(timeLeft)}</span>
            ) : (
              <button
                onClick={handleResendOtp}
                disabled={isLoading}
                className="text-[#3B82F6] hover:text-blue-400 font-semibold transition-colors cursor-pointer disabled:opacity-50"
              >
                Resend OTP
              </button>
            )}
          </div>

          {/* Cancel link for signup flow (matches mobile cancelCallback) */}
          {isSignupFlow && (
            <button
              onClick={() => {
                signupStore.clear();
                navigate({ to: '/sign-up' });
              }}
              className="text-zinc-500 hover:text-zinc-300 font-medium text-sm transition-colors cursor-pointer mt-2"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
